import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Toolbar,
    IconButton,
    Tooltip,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Refresh,
    FilterList,
    Person,
    Work,
    Event,
    CalendarViewMonth,
    Timeline,
    ViewTimeline
} from '@mui/icons-material';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './StaffAvailability.css';

const localizer = momentLocalizer(moment);

const StaffAvailability = ({ onClose }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [users, setUsers] = useState([]);
    const [currentView, setCurrentView] = useState('week');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [displayMode, setDisplayMode] = useState('calendar');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [modalEvent, setModalEvent] = useState(null);

    // Color palette for staff members (vibrant, distinguishable colors)
    const staffColors = [
        '#9c27b0', // Purple
        '#2196f3', // Blue
        '#4caf50', // Green
        '#ff9800', // Orange
        '#f44336', // Red
        '#00bcd4', // Cyan
        '#ffeb3b', // Yellow
        '#795548', // Brown
        '#607d8b', // Blue Grey
        '#e91e63', // Pink
        '#3f51b5', // Indigo
        '#cddc39', // Lime
        '#ff5722', // Deep Orange
        '#009688', // Teal
        '#673ab7', // Deep Purple
        '#ffc107', // Amber
    ];

    // Function to get a consistent color for a staff member
    const getStaffColor = (userId) => {
        if (!userId) return '#666'; // Default grey for no user
        const colorIndex = parseInt(userId) % staffColors.length;
        return staffColors[colorIndex];
    };

    // Helper function to get staff color index
    const getStaffColorIndex = (userId) => {
        if (!userId) return 0;
        return parseInt(userId) % staffColors.length;
    };

    // Helper function to get staff color CSS class
    const getStaffColorClass = (userId) => {
        const colorIndex = getStaffColorIndex(userId);
        return `staff-color-${colorIndex}`;
    };

    // Color mapping for different booking statuses (kept for fallback)
    const statusColors = {
        'booked': '#4CAF50',
        'tentative': '#FF9800',
        'available': '#2196F3',
        'unavailable': '#F44336',
        'break': '#9E9E9E',
        'default': '#666'
    };

    useEffect(() => {
        fetchSchedulerData();
    }, [selectedUser, selectedStatus, currentDate, currentView]);

    const getDateRange = () => {
        const date = moment(currentDate);

        switch (currentView) {
            case 'month':
                return {
                    start: date.clone().startOf('month').subtract(1, 'week').format('YYYY-MM-DD'),
                    end: date.clone().endOf('month').add(1, 'week').format('YYYY-MM-DD')
                };
            case 'week':
                return {
                    start: date.clone().startOf('week').format('YYYY-MM-DD'),
                    end: date.clone().endOf('week').format('YYYY-MM-DD')
                };
            case 'day':
                return {
                    start: date.clone().format('YYYY-MM-DD'),
                    end: date.clone().format('YYYY-MM-DD')
                };
            default:
                return {
                    start: date.clone().startOf('week').format('YYYY-MM-DD'),
                    end: date.clone().endOf('week').format('YYYY-MM-DD')
                };
        }
    };

    const fetchSchedulerData = async () => {
        setLoading(true);
        setError(null);

        try {
            const dateRange = getDateRange();

            const params = {
                start_date: dateRange.start,
                end_date: dateRange.end,
                expand: 'job,users,creator,bookingStatus'
            };

            if (selectedUser) params.user_id = selectedUser;
            if (selectedStatus) params.status = selectedStatus;

            // Debug logging
            console.log('🔍 Fetching scheduler data with params:', {
                dateRange,
                params,
                currentView,
                currentDate: moment(currentDate).format('YYYY-MM-DD')
            });

            const response = await axios.get('/api/trak/scheduler', { params });

            // Debug the full response
            console.log('📡 Full API Response:', {
                status: response.status,
                headers: response.headers,
                data: response.data,
                dataType: typeof response.data,
                isArray: Array.isArray(response.data),
                dataKeys: response.data ? Object.keys(response.data) : 'no data'
            });

            // Handle both possible data structures
            let bookingsData = [];

            if (Array.isArray(response.data)) {
                // Direct array response
                bookingsData = response.data;
                console.log('✅ Processing direct array response:', {
                    totalBookings: bookingsData.length,
                    firstBooking: bookingsData[0],
                    sampleBookings: bookingsData.slice(0, 3)
                });
            } else if (response.data && Array.isArray(response.data.data)) {
                // Wrapped in data property
                bookingsData = response.data.data;
                console.log('✅ Processing wrapped data response:', {
                    totalBookings: bookingsData.length,
                    firstBooking: bookingsData[0],
                    sampleBookings: bookingsData.slice(0, 3)
                });
            } else {
                console.log('❌ Invalid data structure:', {
                    hasData: !!response.data,
                    dataType: typeof response.data,
                    isDataArray: Array.isArray(response.data),
                    hasDataProperty: response.data && 'data' in response.data,
                    isDataPropertyArray: response.data && Array.isArray(response.data.data),
                    actualStructure: response.data
                });
                setEvents([]);
                return;
            }

            if (bookingsData.length > 0) {
                const calendarEvents = bookingsData.map(booking => {
                    console.log('🔄 Processing booking:', {
                        id: booking.id,
                        status: booking.status,
                        start_date: booking.start_date,
                        start_time: booking.start_time,
                        end_date: booking.end_date,
                        end_time: booking.end_time,
                        user: booking.users,
                        job: booking.job
                    });

                    // Combine date and time for proper Date objects
                    const startDateTime = new Date(`${booking.start_date}T${booking.start_time}`);
                    const endDateTime = new Date(`${booking.end_date}T${booking.end_time}`);

                    return {
                        id: booking.id,
                        title: booking.job ?
                            `${booking.job.name || 'Job'} - ${booking.users?.[0]?.first_name || 'Staff'}` :
                            `${booking.users?.[0]?.first_name || 'Staff'} - ${booking.status || 'Booking'}`,
                        start: startDateTime,
                        end: endDateTime,
                        resource: {
                            booking,
                            status: booking.status || 'scheduled',
                            user: booking.users?.[0] || null,
                            job: booking.job || null
                        }
                    };
                });

                console.log('📅 Generated calendar events:', calendarEvents);
                setEvents(calendarEvents);

                // Extract unique users for filter
                const uniqueUsers = [...new Map(
                    bookingsData
                        .filter(booking => booking.users && booking.users.length > 0)
                        .map(booking => [booking.users[0].id, booking.users[0]])
                ).values()];

                console.log('👥 Extracted users:', uniqueUsers);
                setUsers(uniqueUsers);
            } else {
                console.log('📋 No bookings found in the response');
                setEvents([]);
            }
        } catch (err) {
            console.error('💥 Error fetching scheduler data:', {
                error: err,
                message: err.message,
                response: err.response,
                responseData: err.response?.data,
                responseStatus: err.response?.status,
                responseHeaders: err.response?.headers
            });
            setError(`Failed to load staff availability data: ${err.response?.data?.message || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        fetchSchedulerData();
    };

    const handleNavigate = (date, view) => {
        setCurrentDate(date);
        if (view !== currentView) {
            setCurrentView(view);
        }
    };

    const handleViewChange = (view) => {
        setCurrentView(view);
    };

    const handleUserChange = (event) => {
        setSelectedUser(event.target.value);
    };

    // Helper functions for color manipulation
    const lightenColor = (color, amount) => {
        const hex = color.replace('#', '');
        const r = Math.min(255, parseInt(hex.substr(0, 2), 16) + Math.round(255 * amount));
        const g = Math.min(255, parseInt(hex.substr(2, 2), 16) + Math.round(255 * amount));
        const b = Math.min(255, parseInt(hex.substr(4, 2), 16) + Math.round(255 * amount));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    const darkenColor = (color, amount) => {
        const hex = color.replace('#', '');
        const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - Math.round(255 * amount));
        const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - Math.round(255 * amount));
        const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - Math.round(255 * amount));
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    const handleStatusChange = (event) => {
        setSelectedStatus(event.target.value);
    };

    const eventStyleGetter = (event) => {
        const userId = event.resource?.user?.id;
        const primaryColor = getStaffColor(userId);

        // Create gradient variations based on the primary color
        const lighterColor = lightenColor(primaryColor, 0.3);
        const darkerColor = darkenColor(primaryColor, 0.2);

        // Debug logging
        console.log('🎨 Event styling:', {
            eventId: event.id,
            userId,
            userName: event.resource?.user?.first_name,
            primaryColor,
            lighterColor,
            darkerColor,
            title: event.title
        });

        // Use a more specific text color based on background
        const textColor = ['#ffeb3b', '#cddc39', '#ffc107'].includes(primaryColor) ? '#000' : '#fff';

        // Create gradient background
        const gradientBackground = `linear-gradient(135deg, ${lighterColor} 0%, ${primaryColor} 50%, ${darkerColor} 100%)`;

        // Return only inline styles with gradient
        return {
            style: {
                background: gradientBackground,
                backgroundColor: primaryColor, // Fallback
                color: textColor,
                border: `2px solid ${darkerColor}`,
                borderRadius: '8px',
                opacity: 0.95,
                fontWeight: 600,
                fontSize: '12px',
                padding: '3px 8px',
                boxShadow: `0 3px 8px ${primaryColor}40, inset 0 1px 0 ${lighterColor}60`,
                backgroundImage: gradientBackground,
                minHeight: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease'
            }
        };
    };

    const EventComponent = ({ event }) => (
        <Box sx={{ p: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                {event.resource.user?.first_name || event.resource.user?.name || 'Staff'}
            </Typography>
            {event.resource.job && (
                <Typography variant="caption" sx={{ display: 'block', fontSize: '10px' }}>
                    {event.resource.job.name.length > 20
                        ? `${event.resource.job.name.substring(0, 20)}...`
                        : event.resource.job.name}
                </Typography>
            )}
            {event.resource.status && (
                <Chip
                    label={event.resource.status}
                    size="small"
                    sx={{
                        height: '16px',
                        fontSize: '10px',
                        backgroundColor: 'rgba(255,255,255,0.3)'
                    }}
                />
            )}
        </Box>
    );        // Timeline View Component
    const TimelineView = () => {
        // Group events by user
        const eventsByUser = users.reduce((acc, user) => {
            acc[user.id] = events.filter(event => event.resource?.user?.id === user.id);
            return acc;
        }, {});

        // Function to detect overlapping events for a user
        const detectOverlappingEvents = (userEvents) => {
            const sortedEvents = [...userEvents].sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());
            const eventLayers = [];

            sortedEvents.forEach(event => {
                let placed = false;

                // Try to place event in existing layers
                for (let layerIndex = 0; layerIndex < eventLayers.length; layerIndex++) {
                    const layer = eventLayers[layerIndex];
                    const lastEventInLayer = layer[layer.length - 1];

                    // Check if event can be placed in this layer (no overlap)
                    if (moment(event.start).isAfter(moment(lastEventInLayer.end))) {
                        layer.push({ ...event, layerIndex });
                        placed = true;
                        break;
                    }
                }

                // If not placed, create new layer
                if (!placed) {
                    eventLayers.push([{ ...event, layerIndex: eventLayers.length }]);
                }
            });

            // Flatten layers and add layer info
            const eventsWithLayers = [];
            eventLayers.forEach((layer, layerIndex) => {
                layer.forEach(event => {
                    eventsWithLayers.push({
                        ...event,
                        layerIndex,
                        totalLayers: eventLayers.length
                    });
                });
            });

            return eventsWithLayers;
        };

        // Generate time slots based on current view and date
        const generateTimeSlots = () => {
            const slots = [];
            const startHour = 7;
            const endHour = 19;

            if (currentView === 'day') {
                // Start from the beginning of the day
                const dayStart = moment(currentDate).startOf('day');

                // Generate slots for each hour and half hour
                for (let hour = startHour; hour <= endHour; hour++) {
                    // Full hour slot
                    const fullHourTime = dayStart.clone().hour(hour).minute(0);
                    slots.push({
                        time: fullHourTime.format('HH:mm'),
                        timestamp: fullHourTime,
                        isHour: true,
                        isMajor: true
                    });

                    // Half hour slot (except for the last hour)
                    if (hour < endHour) {
                        const halfHourTime = dayStart.clone().hour(hour).minute(30);
                        slots.push({
                            time: halfHourTime.format('HH:mm'),
                            timestamp: halfHourTime,
                            isHour: false,
                            isMajor: false
                        });
                    }
                }
            } else if (currentView === 'week') {
                // Daily slots for week view
                const startOfWeek = moment(currentDate).startOf('week');
                for (let i = 0; i < 7; i++) {
                    const day = startOfWeek.clone().add(i, 'days');
                    slots.push({
                        time: day.format('ddd'),
                        subTime: day.format('MMM DD'),
                        timestamp: day.startOf('day'),
                        isDay: true,
                        isMajor: true
                    });
                }
            } else {
                // Weekly slots for month view
                const startOfMonth = moment(currentDate).startOf('month');
                const endOfMonth = moment(currentDate).endOf('month');
                let current = startOfMonth.clone().startOf('week');
                let weekNumber = 1;

                while (current.isBefore(endOfMonth) || current.isSame(endOfMonth, 'week')) {
                    slots.push({
                        time: `Week ${weekNumber}`,
                        subTime: current.format('MMM DD'),
                        timestamp: current.clone(),
                        isWeek: true,
                        isMajor: true
                    });
                    current.add(1, 'week');
                    weekNumber++;
                }
            }
            return slots;
        };

        const timeSlots = generateTimeSlots();

        // Calculate event position and width based on view
        const calculateEventStyles = (event) => {
            const startTime = moment(event.start);
            const endTime = moment(event.end);

            let containerStart, containerEnd, totalDuration, eventStart, eventDuration;

            if (currentView === 'day') {
                // For day view, align with exact time slots
                containerStart = moment(currentDate).startOf('day').hour(7); // Start at 7:00
                containerEnd = moment(currentDate).startOf('day').hour(19); // End at 19:00
                totalDuration = containerEnd.diff(containerStart, 'minutes');

                const eventStartTime = moment.max(startTime, containerStart);
                const eventEndTime = moment.min(endTime, containerEnd);

                eventStart = eventStartTime.diff(containerStart, 'minutes');
                eventDuration = eventEndTime.diff(eventStartTime, 'minutes');

                // Clamp values to ensure alignment
                eventStart = Math.max(0, eventStart);
                eventDuration = Math.max(0, eventDuration);
            } else if (currentView === 'week') {
                // For week view, use days with better granularity
                containerStart = moment(currentDate).startOf('week');
                containerEnd = moment(currentDate).endOf('week');
                totalDuration = 7; // 7 days

                const eventStartTime = moment.max(startTime, containerStart);
                const eventEndTime = moment.min(endTime, containerEnd);

                eventStart = eventStartTime.diff(containerStart, 'hours') / 24;
                eventDuration = eventEndTime.diff(eventStartTime, 'hours') / 24;

                eventStart = Math.max(0, eventStart);
                eventDuration = Math.max(0, eventDuration);
            } else {
                // For month view, use weeks
                containerStart = moment(currentDate).startOf('month').startOf('week');
                containerEnd = moment(currentDate).endOf('month').endOf('week');
                totalDuration = containerEnd.diff(containerStart, 'weeks');

                const eventStartTime = moment.max(startTime, containerStart);
                const eventEndTime = moment.min(endTime, containerEnd);

                eventStart = eventStartTime.diff(containerStart, 'weeks');
                eventDuration = eventEndTime.diff(eventStartTime, 'weeks');

                eventStart = Math.max(0, eventStart);
                eventDuration = Math.max(0, eventDuration);
            }

            const left = Math.max(0, (eventStart / totalDuration) * 100);
            let width = (eventDuration / totalDuration) * 100;

            // Set minimum and maximum widths based on view
            let minWidth, maxWidth;
            if (currentView === 'day') {
                minWidth = Math.max(2, width); // At least 2% or actual width
                maxWidth = 100 - left;
            } else if (currentView === 'week') {
                minWidth = Math.max(4, width); // At least 4% or actual width
                maxWidth = 100 - left;
            } else {
                minWidth = Math.max(8, width); // At least 8% or actual width
                maxWidth = 100 - left;
            }

            width = Math.min(maxWidth, Math.max(minWidth, width));

            return {
                left: `${left}%`,
                width: `${width}%`,
                minWidth: currentView === 'day' ? '80px' : currentView === 'week' ? '60px' : '40px'
            };
        };

        return (
            <Box sx={{ height: '100%', overflow: 'auto' }}>
                {/* Timeline Navigation */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: '#1a1a1a',
                    borderBottom: '1px solid #333'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                            onClick={() => {
                                const newDate = moment(currentDate).subtract(1, currentView).toDate();
                                setCurrentDate(newDate);
                            }}
                            sx={{ color: '#fff' }}
                        >
                            ←
                        </IconButton>
                        <Typography variant="h6" sx={{ color: '#fff', minWidth: '200px', textAlign: 'center' }}>
                            {currentView === 'day'
                                ? moment(currentDate).format('dddd, MMMM DD, YYYY')
                                : currentView === 'week'
                                    ? `Week of ${moment(currentDate).startOf('week').format('MMM DD')} - ${moment(currentDate).endOf('week').format('MMM DD, YYYY')}`
                                    : moment(currentDate).format('MMMM YYYY')
                            }
                        </Typography>
                        <IconButton
                            onClick={() => {
                                const newDate = moment(currentDate).add(1, currentView).toDate();
                                setCurrentDate(newDate);
                            }}
                            sx={{ color: '#fff' }}
                        >
                            →
                        </IconButton>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setCurrentDate(new Date())}
                            sx={{
                                color: '#fff',
                                borderColor: 'rgba(255,255,255,0.3)',
                                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
                            }}
                        >
                            Today
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#999' }}>
                            {events.length} events • {users.length} staff members
                        </Typography>
                    </Box>
                </Box>

                {/* Timeline Header */}
                <Box sx={{
                    display: 'flex',
                    borderBottom: '2px solid #333',
                    bgcolor: '#2a2a2a',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                }}>
                    {/* Staff column header */}
                    <Box sx={{
                        width: 200,
                        minWidth: 200,
                        p: 2,
                        borderRight: '1px solid #444',
                        bgcolor: '#1a1a1a'
                    }}>
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
                            Staff Members
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                            {currentView.charAt(0).toUpperCase() + currentView.slice(1)} View
                        </Typography>
                    </Box>

                    {/* Time slots header */}
                    <Box sx={{ flex: 1, display: 'flex', position: 'relative' }}>
                        {timeSlots.map((slot, index) => (
                            <Box key={index} sx={{
                                flex: 1,
                                p: 1,
                                borderRight: index < timeSlots.length - 1 ? '1px solid #444' : 'none',
                                textAlign: 'center',
                                minWidth: currentView === 'day' ? '80px' : currentView === 'week' ? '100px' : '120px',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                            }}>
                                <Typography variant="caption" sx={{
                                    color: '#ccc',
                                    fontWeight: 'bold',
                                    fontSize: currentView === 'day' ? '11px' : '12px'
                                }}>
                                    {slot.time}
                                </Typography>
                                {slot.subTime && (
                                    <Typography variant="caption" sx={{
                                        display: 'block',
                                        color: '#999',
                                        fontSize: '10px',
                                        mt: 0.5
                                    }}>
                                        {slot.subTime}
                                    </Typography>
                                )}
                                {/* Add time reference markers for better visual clarity */}
                                {currentView === 'day' && slot.isMajor && (
                                    <Box sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '2px',
                                        height: '4px',
                                        bgcolor: '#666'
                                    }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Staff Rows */}
                {users.map((user) => {
                    const userEvents = eventsByUser[user.id] || [];
                    const userColor = getStaffColor(user.id);

                    // Detect overlapping events and calculate layers
                    const eventsWithLayers = detectOverlappingEvents(userEvents);
                    const maxLayers = Math.max(1, ...eventsWithLayers.map(e => e.totalLayers || 1));

                    // Adjust row height based on view and number of overlapping layers
                    const baseRowHeight = currentView === 'day' ? 80 : currentView === 'week' ? 60 : 50;
                    const layerHeight = currentView === 'day' ? 35 : currentView === 'week' ? 28 : 22;
                    const rowHeight = baseRowHeight + (maxLayers - 1) * layerHeight;

                    return (
                        <Box key={user.id} sx={{
                            display: 'flex',
                            minHeight: rowHeight,
                            borderBottom: '1px solid #333',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }
                        }}>
                            {/* Staff Info */}
                            <Box sx={{
                                width: 200,
                                minWidth: 200,
                                p: 2,
                                borderRight: '1px solid #444',
                                bgcolor: '#252525',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                minHeight: rowHeight
                            }}>
                                <Box sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: userColor,
                                    flexShrink: 0
                                }} />
                                <Box sx={{ overflow: 'hidden' }}>
                                    <Typography variant="body2" sx={{
                                        color: '#fff',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {user.first_name || user.name || `User ${user.id}`}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#999' }}>
                                        {userEvents.length} event{userEvents.length !== 1 ? 's' : ''}
                                        {maxLayers > 1 && ` (${maxLayers} layers)`}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Timeline Events */}
                            <Box sx={{
                                flex: 1,
                                position: 'relative',
                                minHeight: rowHeight,
                                display: 'flex',
                                alignItems: 'stretch',
                                overflow: 'visible'
                            }}>
                                {/* Time slot background */}
                                <Box sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex'
                                }}>
                                    {timeSlots.map((slot, index) => (
                                        <Box key={index} sx={{
                                            flex: 1,
                                            borderRight: index < timeSlots.length - 1 ? '1px solid #333' : 'none',
                                            '&:nth-of-type(even)': { bgcolor: 'rgba(255,255,255,0.01)' },
                                            minWidth: currentView === 'day' ? '80px' : currentView === 'week' ? '100px' : '120px'
                                        }} />
                                    ))}
                                </Box>

                                {/* Events with layering for overlaps */}
                                {eventsWithLayers.map((event, index) => {
                                    const styles = calculateEventStyles(event);
                                    const eventColor = getStaffColor(user.id);
                                    const darkerColor = darkenColor(eventColor, 0.2);
                                    const lighterColor = lightenColor(eventColor, 0.3);

                                    // Calculate event height and position based on layer and content
                                    const baseEventHeight = currentView === 'day' ? 30 : currentView === 'week' ? 24 : 18;
                                    const contentHeight = Math.max(baseEventHeight,
                                        currentView === 'day' ? 45 : // Allow more height in day view
                                            currentView === 'week' ? 35 : // Medium height in week view
                                                25 // Minimum height in month view
                                    );
                                    const layerOffset = event.layerIndex * (contentHeight + 4);
                                    const topPosition = Math.min(10 + layerOffset, rowHeight - contentHeight - 10);
                                    const eventHeight = contentHeight;

                                    // Adjust opacity and styling for overlapping events
                                    const isOverlapping = event.totalLayers > 1;
                                    const overlappingOpacity = isOverlapping ? 0.9 - (event.layerIndex * 0.1) : 1;

                                    // Use black text for all events for consistent readability
                                    const textColor = '#000000';

                                    return (
                                        <Box key={`${event.id}-${index}`} onClick={() => handleEventClick(event)} sx={{
                                            position: 'absolute',
                                            top: topPosition,
                                            ...styles,
                                            height: eventHeight,
                                            background: `linear-gradient(135deg, ${lighterColor} 0%, ${eventColor} 50%, ${darkerColor} 100%)`,
                                            borderRadius: currentView === 'day' ? '6px' : '4px',
                                            border: `1px solid ${darkerColor}`,
                                            padding: currentView === 'day' ? '4px 8px' : '2px 6px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            color: textColor,
                                            cursor: 'pointer',
                                            zIndex: 5 + event.layerIndex,
                                            opacity: overlappingOpacity,
                                            boxShadow: isOverlapping
                                                ? `0 2px 6px ${eventColor}50, inset 0 1px 0 ${lighterColor}40`
                                                : `0 2px 4px ${eventColor}40`,
                                            // Add a subtle text background for better readability
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.3) 100%)',
                                                borderRadius: 'inherit',
                                                pointerEvents: 'none'
                                            },
                                            '&:hover': {
                                                transform: 'scale(1.02)',
                                                zIndex: 20,
                                                opacity: 1,
                                                boxShadow: `0 4px 12px ${eventColor}70`,
                                                '&::before': {
                                                    background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.4) 100%)'
                                                }
                                            },
                                            transition: 'all 0.2s ease'
                                        }}>
                                            <Typography variant="caption" sx={{
                                                fontWeight: 'bold',
                                                fontSize: currentView === 'day' ? '11px' : currentView === 'week' ? '10px' : '9px',
                                                lineHeight: 1.2,
                                                overflow: 'hidden',
                                                position: 'relative',
                                                zIndex: 1,
                                                color: '#000000 !important',
                                                wordWrap: 'break-word',
                                                whiteSpace: 'normal',
                                                display: 'block',
                                                mb: 0.5
                                            }}>
                                                {event.resource.job?.name || event.title}
                                            </Typography>
                                            {currentView !== 'month' && eventHeight > 20 && (
                                                <Typography variant="caption" sx={{
                                                    display: 'block',
                                                    fontSize: currentView === 'day' ? '9px' : '8px',
                                                    opacity: 0.95,
                                                    lineHeight: 1.1,
                                                    overflow: 'hidden',
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    color: '#000000 !important',
                                                    wordWrap: 'break-word',
                                                    whiteSpace: currentView === 'day' ? 'normal' : 'normal',
                                                    textOverflow: currentView === 'day' ? 'clip' : 'ellipsis'
                                                }}>
                                                    {currentView === 'day'
                                                        ? `${moment(event.start).format('HH:mm')} - ${moment(event.end).format('HH:mm')}`
                                                        : moment(event.start).format('MMM DD HH:mm')
                                                    }
                                                </Typography>
                                            )}
                                            {/* Layer indicator for overlapping events */}
                                            {isOverlapping && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: 2,
                                                    right: 2,
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: 'rgba(255,255,255,0.9)',
                                                    color: '#000',
                                                    fontSize: '8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontWeight: 'bold',
                                                    border: '1px solid rgba(0,0,0,0.3)',
                                                    textShadow: 'none',
                                                    zIndex: 2
                                                }}>
                                                    {event.layerIndex + 1}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    );
                })}

                {/* Empty state */}
                {users.length === 0 && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 200,
                        color: '#666'
                    }}>
                        <Typography variant="body1">
                            No staff members found for the selected date range
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    const handleEventClick = (event) => {
        setModalEvent(event);
        setDialogOpen(true);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading staff availability...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                {error}
                <IconButton onClick={handleRefresh} sx={{ ml: 1 }}>
                    <Refresh />
                </IconButton>
            </Alert>
        );
    }

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#1a1a1a', color: 'white' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Event />
                        Staff Availability
                    </Typography>
                    <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh} sx={{ color: 'white' }}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Test API Connection">
                        <IconButton onClick={async () => {
                            try {
                                console.log('🧪 Testing API connection...');
                                const response = await axios.get('/api/trak/scheduler?start_date=2025-05-01&end_date=2025-05-31');
                                console.log('🧪 Test API Response:', response);
                                alert(`API Test: ${response.status} - Check console for details`);
                            } catch (err) {
                                console.error('🧪 API Test Error:', err);
                                alert(`API Test Failed: ${err.message} - Check console for details`);
                            }
                        }} sx={{ color: 'orange' }}>
                            🧪
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel sx={{ color: 'white' }}>Filter by Staff</InputLabel>
                        <Select
                            value={selectedUser}
                            label="Filter by Staff"
                            onChange={handleUserChange}
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                                '& .MuiSvgIcon-root': { color: 'white' }
                            }}
                        >
                            <MenuItem value="">All Staff</MenuItem>
                            {users.map(user => (
                                <MenuItem key={user.id} value={user.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Person fontSize="small" />
                                        {user.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel sx={{ color: 'white' }}>Filter by Status</InputLabel>
                        <Select
                            value={selectedStatus}
                            label="Filter by Status"
                            onChange={handleStatusChange}
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                                '& .MuiSvgIcon-root': { color: 'white' }
                            }}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="booked">Booked</MenuItem>
                            <MenuItem value="tentative">Tentative</MenuItem>
                            <MenuItem value="available">Available</MenuItem>
                            <MenuItem value="unavailable">Unavailable</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Date Range Display */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        ml: 2,
                        px: 2,
                        py: 1,
                        bgcolor: 'rgba(255,255,255,0.1)',
                        borderRadius: 1
                    }}>
                        <Event fontSize="small" />
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {(() => {
                                const dateRange = getDateRange();
                                const start = moment(dateRange.start).format('MMM DD');
                                const end = moment(dateRange.end).format('MMM DD, YYYY');
                                return `${start} - ${end}`;
                            })()}
                        </Typography>
                    </Box>

                    {/* View Toggle */}
                    <ToggleButtonGroup
                        value={displayMode}
                        exclusive
                        onChange={(event, newMode) => {
                            if (newMode !== null) {
                                setDisplayMode(newMode);
                            }
                        }}
                        size="small"
                        sx={{
                            ml: 2,
                            '& .MuiToggleButton-root': {
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.3)',
                                '&.Mui-selected': {
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: '#1565c0'
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }
                        }}
                    >
                        <ToggleButton value="calendar" aria-label="calendar view">
                            <CalendarViewMonth sx={{ mr: 1, fontSize: '18px' }} />
                            Calendar
                        </ToggleButton>
                        <ToggleButton value="timeline" aria-label="timeline view">
                            <ViewTimeline sx={{ mr: 1, fontSize: '18px' }} />
                            Timeline
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Timeline View Controls */}
                    {displayMode === 'timeline' && (
                        <ToggleButtonGroup
                            value={currentView}
                            exclusive
                            onChange={(event, newView) => {
                                if (newView !== null) {
                                    handleViewChange(newView);
                                }
                            }}
                            size="small"
                            sx={{
                                ml: 2,
                                '& .MuiToggleButton-root': {
                                    color: 'white',
                                    borderColor: 'rgba(255,255,255,0.3)',
                                    '&.Mui-selected': {
                                        backgroundColor: '#4caf50',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: '#388e3c'
                                        }
                                    },
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.1)'
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="day" aria-label="day view">
                                Day
                            </ToggleButton>
                            <ToggleButton value="week" aria-label="week view">
                                Week
                            </ToggleButton>
                            <ToggleButton value="month" aria-label="month view">
                                Month
                            </ToggleButton>
                        </ToggleButtonGroup>
                    )}

                    {/* Staff Color Legend */}
                    <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap', maxWidth: '50%' }}>
                        {users.slice(0, 8).map((user) => (
                            <Chip
                                key={user.id}
                                label={user.first_name || user.name || `User ${user.id}`}
                                size="small"
                                sx={{
                                    backgroundColor: getStaffColor(user.id),
                                    color: 'white',
                                    fontSize: '11px',
                                    height: '24px',
                                    maxWidth: '100px'
                                }}
                            />
                        ))}
                        {users.length > 8 && (
                            <Chip
                                label={`+${users.length - 8} more`}
                                size="small"
                                sx={{
                                    backgroundColor: '#666',
                                    color: 'white',
                                    fontSize: '11px',
                                    height: '24px'
                                }}
                            />
                        )}
                    </Box>
                </Box>

                {/* Debug Info (remove in production) */}
                <Box sx={{
                    mt: 2,
                    p: 1,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 1,
                    fontSize: '12px',
                    fontFamily: 'monospace'
                }}>
                    <Typography variant="caption" sx={{ display: 'block', color: '#ffeb3b' }}>
                        🐛 Debug Info: View={currentView} | Date={moment(currentDate).format('YYYY-MM-DD')} | Events={events.length}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#ffeb3b' }}>
                        📅 Date Range: {(() => {
                            const range = getDateRange();
                            return `${range.start} to ${range.end}`;
                        })()}
                    </Typography>
                </Box>
            </Paper>

            {/* Main Content - Calendar or Timeline View */}
            <Paper sx={{ flex: 1, p: displayMode === 'timeline' ? 0 : 2, overflow: 'hidden' }}>
                {displayMode === 'calendar' ? (
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: '100%' }}
                        eventPropGetter={eventStyleGetter}
                        components={{
                            event: EventComponent
                        }}
                        defaultView="week"
                        view={currentView}
                        date={currentDate}
                        onNavigate={handleNavigate}
                        onView={handleViewChange}
                        views={['month', 'week', 'day']}
                        step={60}
                        showMultiDayTimes
                        popup
                        onSelectEvent={handleEventClick}
                        formats={{
                            timeGutterFormat: 'HH:mm',
                            eventTimeRangeFormat: ({ start, end }) =>
                                `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`
                        }}
                        min={moment().hour(7).minute(0).toDate()}
                        max={moment().hour(19).minute(0).toDate()}
                    />
                ) : (
                    <TimelineView />
                )}
            </Paper>

            {/* Event Details Modal */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>Event Details</DialogTitle>
                <DialogContent>
                    <Typography><strong>Title:</strong> {modalEvent?.resource?.job?.name || modalEvent?.title}</Typography>
                    <Typography><strong>User:</strong> {modalEvent?.resource?.user?.first_name || modalEvent?.resource?.user?.name}</Typography>
                    <Typography><strong>Start:</strong> {modalEvent && moment(modalEvent.start).format('YYYY-MM-DD HH:mm')}</Typography>
                    <Typography><strong>End:</strong> {modalEvent && moment(modalEvent.end).format('YYYY-MM-DD HH:mm')}</Typography>
                    <Typography><strong>Status:</strong> {modalEvent?.resource?.status}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StaffAvailability;
